import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  FaUpload,
  FaTrash,
  FaExclamationTriangle,
  FaPlus,
} from "react-icons/fa";
import { mediaService, MediaFile } from "../../services/firebase";
import "./Media.css";
import PrimaryButton from "components/PrimaryButton";

const AdminMedia: React.FC = () => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [orderValues, setOrderValues] = useState<{ [key: string]: number }>({});

  const fetchFiles = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await mediaService.getAll();
      // סינון רק תמונות גלריה
      const galleryFiles = data.filter(
        (file) =>
          file.type.startsWith("image/") &&
          (file.category === "gallery" || !file.category)
      );
      setFiles(galleryFiles);
    } catch (error) {
            setError(t("admin.loadError"));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    if (files.length > 0) {
      const initialOrderValues: { [key: string]: number } = {};
      files.forEach((file, index) => {
        if (file.id) {
          // נשתמש במיקום הנוכחי במערך הממוין (1-based)
          initialOrderValues[file.id] = index + 1;
        }
      });
      setOrderValues(initialOrderValues);
    }
  }, [files]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      let uploadedCount = 0;
      for (let i = 0; i < selectedFiles.length; i++) {
        try {
          await mediaService.upload(selectedFiles[i], "gallery");
          uploadedCount++;
        } catch (uploadError) {
                    setError(`שגיאה בהעלאת הקובץ ${selectedFiles[i].name}`);
          break;
        }
      }

      if (uploadedCount > 0) {
        setSuccess(
          `${t("admin.uploadSuccess")} ${uploadedCount} ${t("admin.files")}`
        );
        fetchFiles();
      }
    } catch (error) {
            setError(t("admin.uploadError"));
    } finally {
      setIsUploading(false);
      // ניקוי השדה
      e.target.value = "";
    }
  };

  const handleDelete = async (id: string, fileName: string) => {
    if (window.confirm(`${t("admin.confirmDelete")} "${fileName}"?`)) {
      try {
        setError(null);
        await mediaService.delete(id);
        setSuccess(t("admin.fileDeleted"));
        fetchFiles();
      } catch (error) {
                setError(t("admin.deleteError"));
      }
    }
  };

  const handleOrderChange = async (fileId: string, newPosition: number) => {
    // בדיקת תקינות המספר
    if (
      newPosition < 1 ||
      newPosition > files.length ||
      !Number.isInteger(newPosition)
    ) {
      alert(`אנא הזן מספר בין 1 ל-${files.length}`);
      return;
    }

    const currentIndex = files.findIndex((f) => f.id === fileId);
    if (currentIndex === -1) return;

    // המרה לאינדקס (0-based)
    const targetIndex = newPosition - 1;

    // אם המיקום לא השתנה, אין צורך לעדכן
    if (currentIndex === targetIndex) return;

    const newFiles = [...files];
    const [movedFile] = newFiles.splice(currentIndex, 1);
    newFiles.splice(targetIndex, 0, movedFile);

    // עדכון displayOrder - שומרים כ-1-based (1, 2, 3...)
    const reorderMap = newFiles.map((f, index) => ({
      id: f.id!,
      displayOrder: index + 1, // שינוי: index + 1 במקום index
    }));

    try {
      await mediaService.reorderMedia(reorderMap);

      // עדכון displayOrder בכל קובץ במערך החדש - גם כאן 1-based
      const updatedFiles = newFiles.map((file, index) => ({
        ...file,
        displayOrder: index + 1, // שינוי: index + 1 במקום index
      }));

      setFiles(updatedFiles);
      // עדכון ערכי הסדר לאחר השינוי
      const updatedOrderValues: { [key: string]: number } = {};
      updatedFiles.forEach((file, idx) => {
        if (file.id) {
          updatedOrderValues[file.id] = idx + 1;
        }
      });
      setOrderValues(updatedOrderValues);
      setSuccess(t("admin.orderUpdated"));
    } catch (error) {
            setError(t("admin.orderUpdateError"));
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (files.length === 0 && !isUploading && !isLoading) {
    return (
      <div className="page-media-admin-container">
        <div className="header-media-admin  ">
          <h1 className="title-media-admin">{t("admin.media")}</h1>
          <label className="upload-button-media-admin">
            <FaPlus />
            {t("admin.uploadMedia")}
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileChange}
              disabled={isUploading}
            />
          </label>
        </div>

        <div className="empty-state-media-admin">
          <h3>{t("admin.noMediaFiles")}</h3>
          <p>{t("admin.startUploading")}</p>
          <div
            className="upload-area-media-admin"
            onClick={() => {
              const input = document.querySelector(
                ".upload-button input"
              ) as HTMLInputElement;
              input?.click();
            }}
          >
            <FaUpload />
            <div className="upload-area-text-media-admin">
              {t("admin.clickToUpload")}
            </div>
            <div className="upload-area-subtext-media-admin">
              {t("admin.dragAndDrop")}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-media-admin-container">
      <div className="header-media-admin">
        <h1 className="title-media-admin">{t("admin.media")}</h1>
        <label className="upload-button-media-admin">
          <FaPlus />
          {t("admin.uploadMedia")}
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            disabled={isUploading}
          />
        </label>
      </div>

      {error && (
        <div className="error-message-media-admin">
          <FaExclamationTriangle />
          {error}
        </div>
      )}

      {success && (
        <div className="success-message-media-admin">
          <FaUpload />
          {success}
        </div>
      )}

      {isLoading && (
        <div className="loading-overlay-media-admin">
          <div className="loading-message-media-admin">
            {t("admin.loading")}
          </div>
        </div>
      )}

      <div className="media-grid-media-admin  ">
        {files.map((file, index) => (
          <div className="media-card-media-admin" key={file.id}>
            <div className="media-order-controls">
              <label className="order-label" htmlFor={`order-${file.id}`}>
                מיקום:
              </label>
              <input
                key={`order-${file.id}`}
                id={`order-${file.id}`}
                type="number"
                className="order-input"
                min="1"
                max={files.length}
                value={orderValues[file.id!] ?? index + 1}
                onChange={(e) => {
                  const value = e.target.value;
                  const numValue = parseInt(value);
                  if (
                    value === "" ||
                    (numValue >= 1 && numValue <= files.length)
                  ) {
                    setOrderValues((prev) => ({
                      ...prev,
                      [file.id!]: value === "" ? index + 1 : numValue,
                    }));
                  }
                }}
                onBlur={(e) => {
                  const newPosition = parseInt(e.target.value);
                  if (!isNaN(newPosition) && file.id) {
                    handleOrderChange(file.id, newPosition);
                  } else {
                    // אם הערך לא תקין, נחזיר לערך המקורי
                    setOrderValues((prev) => ({
                      ...prev,
                      [file.id!]: file.displayOrder ?? index + 1,
                    }));
                  }
                }}
              />
            </div>
            <img
              className="media-image-media-admin  "
              src={file.url}
              alt={file.name}
              onError={(e) => {
                // אם התמונה לא נטענת, הצג תמונת ברירת מחדל
                const target = e.target as HTMLImageElement;
                target.src = "/images/image-error.jpg";
                target.onerror = null; // מניעת לולאה אינסופית
              }}
            />
            <div className="media-overlay-media-admin">
              <button
                className="delete-button-media-admin"
                onClick={() => file.id && handleDelete(file.id, file.name)}
              >
                <FaTrash />
              </button>
            </div>
            <div className="media-info-media-admin">
              <div className="media-name">{file.name}</div>
              <div className="media-size-media-admin">
                {formatFileSize(file.size)}
              </div>
              <PrimaryButton
                className="delete-button-media-admin"
                onClick={() => file.id && handleDelete(file.id, file.name)}
                icon={<FaTrash />}
                fullWidth={true}
              >
                {t("admin.delete")}
              </PrimaryButton>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminMedia;
